import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Navbar from '../components/Navbar';
import { trackToolAction } from '../utils/metrics';
import { parsePhoneList, lookupFoursquareBatch } from '../utils/phoneIntel';
import { extractTextFromFile } from '../utils/extractText';

// Commercial Phone Number Lookup.
//
// 100% local, like the rest of LE Tech Tools: numbers are checked against
// Foursquare Open Source Places, a ~15M-record US business dataset shipped
// as a static asset with this site (see phoneIntel.js). A lookup fetches
// only the area-code data file(s) a paste actually needs, from this app's
// own origin — same as loading any other page asset. The phone number
// itself is never sent anywhere.
//
// This can't identify a personal caller — that's CNAM/carrier data behind
// paid APIs that need a secret key on a server, which this app doesn't have.

const INK = '#0f172a';
const MUTED = '#64748b';
const BORDER = '#e2e8f0';

function Chevron({ open }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
      style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}>
      <polyline points="9 6 15 12 9 18" />
    </svg>
  );
}

function ResultRow({ row, onExpand, expanded }) {
  const matches = row.matches || [];
  const hasAny = matches.length > 0;
  return (
    <>
      <tr
        className="phone-row"
        onClick={onExpand}
        style={{ cursor: hasAny ? 'pointer' : 'default', borderTop: `1px solid ${BORDER}` }}
      >
        <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: 13, whiteSpace: 'nowrap' }}>{row.display}</td>
        <td style={{ padding: '10px 12px', fontSize: 12.5, color: MUTED }}>{row.input}</td>
        <td style={{ padding: '10px 12px', fontSize: 12.5 }}>
          {row.status === 'loading'
            ? <span style={{ color: MUTED }}>Checking…</span>
            : row.error
              ? <span style={{ color: '#b91c1c' }} title={row.error}>Lookup failed</span>
              : hasAny
                ? <span style={{ color: '#166534', fontWeight: 700 }}>{matches.length} match{matches.length === 1 ? '' : 'es'}</span>
                : <span style={{ color: MUTED }}>No match</span>}
        </td>
        <td style={{ padding: '10px 12px', textAlign: 'right' }}>
          {hasAny && <Chevron open={expanded} />}
        </td>
      </tr>
      {expanded && hasAny && (
        <tr style={{ background: '#f8fafc' }}>
          <td colSpan={4} style={{ padding: '10px 16px 16px' }}>
            {matches.length > 1 && (
              <p style={{ margin: '0 0 8px', fontSize: 11.5, color: '#92400e' }}>
                More than one business has been recorded at this number — sorted most-recently-confirmed first, but
                Foursquare doesn't always mark an old listing closed when a new business takes over a number.
              </p>
            )}
            {matches.map((m, i) => (
              <div key={i} style={{ padding: '8px 0', borderBottom: i < matches.length - 1 ? `1px solid ${BORDER}` : 'none' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: INK }}>{m.name}</div>
                  {matches.length > 1 && i === 0 && (
                    <span style={{
                      fontSize: 9.5, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase',
                      background: '#dcfce7', color: '#166534', padding: '1px 6px', borderRadius: 20,
                    }}>
                      Most recent
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: MUTED }}>
                  {[m.type, m.address].filter(Boolean).join(' · ')}
                  {m.mapUrl && <> · <a href={m.mapUrl} target="_blank" rel="noreferrer">map</a></>}
                </div>
                {m.refreshed && (
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                    Last confirmed {new Date(m.refreshed).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                  </div>
                )}
              </div>
            ))}
          </td>
        </tr>
      )}
    </>
  );
}

export default function PhoneLookup() {
  const [text, setText] = useState('');
  const [entries, setEntries] = useState([]);
  const [matchByDigits, setMatchByDigits] = useState({});
  const [errorByDigits, setErrorByDigits] = useState({});
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [notice, setNotice] = useState('');
  const [fileBusy, setFileBusy] = useState(false);
  const abortRef = useRef(null);

  useEffect(() => () => { if (abortRef.current) abortRef.current.abort(); }, []);

  // Uploaded content is only ever appended to the textarea — you still press
  // Look up yourself, same as pasting text in directly.
  const handleFileUpload = useCallback(async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setFileBusy(true);
    try {
      const extracted = await extractTextFromFile(file);
      const { entries: found } = parsePhoneList(extracted);
      setText(prev => (prev.trim() ? `${prev}\n${extracted}` : extracted));
      setNotice(`${file.name}: found ${found.length} phone number${found.length === 1 ? '' : 's'}. Press Look up to run it.`);
    } catch (err) {
      setNotice(`Could not read ${file.name}: ${err.message}`);
    } finally {
      setFileBusy(false);
    }
  }, []);

  const runLookup = useCallback(() => {
    const { entries: found, duplicateCount } = parsePhoneList(text);
    if (!found.length) {
      setNotice('No phone numbers found in that input.');
      return;
    }
    trackToolAction('/phonelookup', 'Commercial Phone Number Lookup');
    setEntries(found);
    setMatchByDigits({});
    setErrorByDigits({});
    setExpanded(found.length === 1 ? found[0].digits : null);
    setNotice(duplicateCount ? `${found.length} unique numbers (${duplicateCount} duplicate${duplicateCount === 1 ? '' : 's'} collapsed).` : '');

    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setLoading(true);
    lookupFoursquareBatch(found.map(e => e.digits), {
      signal: ctrl.signal,
      onResult: (digits, { matches, error }) => {
        setMatchByDigits(prev => ({ ...prev, [digits]: matches }));
        if (error) setErrorByDigits(prev => ({ ...prev, [digits]: error }));
      },
    }).finally(() => setLoading(false));
  }, [text]);

  const rows = useMemo(() => entries.map(e => ({
    ...e,
    matches: matchByDigits[e.digits],
    error: errorByDigits[e.digits],
    status: matchByDigits[e.digits] === undefined && loading ? 'loading' : 'done',
  })), [entries, matchByDigits, errorByDigits, loading]);

  const matchCount = rows.filter(r => r.matches && r.matches.length).length;

  const exportCsv = useCallback(() => {
    const hdrs = ['Input', 'Number', 'Matches', 'Addresses', 'Last confirmed (most recent first)', 'Error'];
    const body = rows.map(r => [
      r.input, r.display,
      (r.matches || []).map(m => m.name).join(' | '),
      (r.matches || []).map(m => m.address).filter(Boolean).join(' | '),
      (r.matches || []).map(m => m.refreshed || '?').join(' | '),
      r.error || '',
    ]);
    const csv = [hdrs, ...body]
      .map(row => row.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const a = Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(new Blob([csv], { type: 'text/csv' })),
      download: 'phone-lookup-export.csv',
    });
    a.click();
    URL.revokeObjectURL(a.href);
  }, [rows]);

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", color: INK }}>
      <Navbar pageTitle="Commercial Phone Number Lookup" />
      <style>{`
        .phone-row:hover { background: #f8fafc !important; }
      `}</style>

      <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 24px 72px' }}>
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 10,
          background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#15803d',
          borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: 13, lineHeight: 1.55,
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
            style={{ flexShrink: 0, marginTop: 1 }}>
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
          <span>
            <strong>100% local &amp; private.</strong> Numbers are checked against a business dataset built into
            this site — the number itself is never sent anywhere. It can't identify who a personal number
            belongs to; that requires legal process to the carrier.
          </span>
        </div>

        <div style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 12, padding: '18px 20px', marginBottom: 20 }}>
          <label htmlFor="phone-input" style={{ display: 'block', fontSize: 13, fontWeight: 700, marginBottom: 8 }}>
            Phone numbers
          </label>
          <textarea
            id="phone-input"
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder={'(914) 555-1234\n917-555-9876\n\nPaste one per line, comma-separated, or an entire report — numbers are extracted automatically and de-duplicated.'}
            rows={6}
            style={{
              width: '100%', boxSizing: 'border-box', resize: 'vertical',
              border: `1px solid ${BORDER}`, borderRadius: 8, padding: '10px 12px',
              fontSize: 13, fontFamily: 'monospace', lineHeight: 1.6, color: INK, background: '#f8fafc',
            }}
          />

          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginTop: 10 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: fileBusy ? 'default' : 'pointer' }}>
              <span style={{
                border: `1px solid ${BORDER}`, borderRadius: 6, padding: '6px 12px', fontSize: 12.5,
                fontWeight: 600, color: fileBusy ? '#94a3b8' : '#475569', background: '#fff',
              }}>
                {fileBusy ? 'Reading…' : 'Upload file'}
              </span>
              <input
                type="file"
                accept=".txt,.csv,.xlsx"
                disabled={fileBusy}
                onChange={handleFileUpload}
                style={{ display: 'none' }}
              />
            </label>
            <span style={{ fontSize: 12, color: '#94a3b8' }}>.txt, .csv, or .xlsx — numbers are extracted automatically and added to the box above</span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginTop: 12 }}>
            <button
              onClick={runLookup}
              disabled={!text.trim() || loading}
              style={{
                background: text.trim() && !loading ? '#4f46e5' : '#cbd5e1', color: '#fff', border: 'none',
                borderRadius: 8, padding: '9px 20px', fontSize: 14, fontWeight: 700,
                cursor: text.trim() && !loading ? 'pointer' : 'default',
              }}
            >
              {loading ? 'Looking up…' : 'Look up'}
            </button>
            {entries.length > 0 && (
              <button
                onClick={() => { setEntries([]); setText(''); setMatchByDigits({}); setErrorByDigits({}); setExpanded(null); setNotice(''); }}
                style={{ background: 'none', border: 'none', color: MUTED, fontSize: 13, cursor: 'pointer', textDecoration: 'underline' }}
              >
                Clear
              </button>
            )}
          </div>
          {notice && <p style={{ margin: '10px 0 0', fontSize: 12.5, color: MUTED }}>{notice}</p>}
        </div>

        {entries.length > 0 && (
          <>
            <div style={{
              display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10,
              background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 12,
              padding: '12px 16px', marginBottom: 12,
            }}>
              <strong style={{ fontSize: 14 }}>{entries.length} number{entries.length === 1 ? '' : 's'}</strong>
              <span style={{ fontSize: 12.5, color: MUTED }}>{matchCount} with a match</span>
              <button
                onClick={exportCsv}
                style={{ marginLeft: 'auto', background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 7, padding: '7px 14px', fontSize: 12.5, fontWeight: 600, color: '#475569', cursor: 'pointer' }}
              >
                Export CSV
              </button>
            </div>

            <div style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 12, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                    {['Number', 'As pasted', 'Business match', ''].map(h => (
                      <th key={h} style={{ padding: '9px 12px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: MUTED }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map(row => (
                    <ResultRow
                      key={row.digits}
                      row={row}
                      expanded={expanded === row.digits}
                      onExpand={() => setExpanded(cur => (cur === row.digits ? null : row.digits))}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        <p style={{ marginTop: 24, fontSize: 12, color: '#94a3b8', lineHeight: 1.6 }}>
          A match here identifies a mapped business that lists this number — it does not identify who a
          personal cell or landline belongs to. That data (caller-ID name / CNAM) is held by carriers and
          requires legal process. A missing match proves nothing either way: the dataset is crowdsourced and
          doesn't have perfect coverage.
        </p>
        <p style={{ marginTop: 8, fontSize: 11, color: '#cbd5e1', lineHeight: 1.6 }}>
          Business data from <a href="https://opensource.foursquare.com/os-places/" target="_blank" rel="noreferrer" style={{ color: '#94a3b8' }}>Foursquare Open Source Places</a>,
          © Foursquare Labs, Inc., used and modified under the{' '}
          <a href="https://www.apache.org/licenses/LICENSE-2.0" target="_blank" rel="noreferrer" style={{ color: '#94a3b8' }}>Apache License 2.0</a>{' '}
          (<a href="/data/foursquare-phones/NOTICE.txt" target="_blank" rel="noreferrer" style={{ color: '#94a3b8' }}>notice</a>).
        </p>
      </div>
    </div>
  );
}
